import {
  Stack,
  Card,
  Table,
  Badge,
  Button,
  Group,
  Text,
  TextInput,
  Alert,
  ActionIcon,
  Tooltip,
  Select,
  Pagination,
  Center,
  Loader,
} from '@mantine/core';
import {
  IconDownload,
  IconAlertCircle,
  IconEye,
  IconFileCertificate,
  IconSearch,
} from '@tabler/icons-react';
import { useNavigate, Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useDeCAs } from '../../api/hooks';
import type { DeCaEnriched } from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';

const estadoColor: Record<string, string> = {
  VIGENTE: 'green',
  EN_CURSO: 'blue',
  FINALIZADO: 'gray',
  CANCELADO: 'red',
};

// Estados que admitimos como DeCA. Mantener sincronizado con `DeCaEnriched`.
// Usamos el conjunto en el Select de filtro. Sprint 7.2: 4 estados reales.
const ESTADOS_DECA: DeCaEnriched['estado'][] = ['VIGENTE', 'EN_CURSO', 'FINALIZADO', 'CANCELADO'];

const PAGE_SIZE = 20;

export function DeCAList() {
  const navigate = useNavigate();
  const { data: decas = [], isLoading, error } = useDeCAs();
  const [filter, setFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    return decas.filter((d) => {
      if (estadoFilter && d.estado !== estadoFilter) return false;
      if (!q) return true;
      const hay = [
        d.codigoAnual,
        d.codigoPublico,
        d.estado,
        d.servicios?.[0]?.origen?.nombre,
        d.servicios?.[0]?.origen?.poblacion,
        d.servicios?.[0]?.destino?.nombre,
        d.servicios?.[0]?.destino?.poblacion,
        d.cargador?.razonSocial,
        d.transportista?.razonSocial,
        d.vehiculo?.matricula,
        d.conductor?.nombre,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [decas, filter, estadoFilter]);

  // Reset a la primera pagina cuando cambian los filtros (efecto de lado).
  // Ademas, `safePage` acota `page` al rango valido en cada render — asi si
  // el filtro reduce el total por debajo de la pagina actual, no dejamos
  // la paginacion mostrando "pagina 4 de 2".
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filter, estadoFilter]);

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title="DeCA emitidos"
        subtitle="Documentos electronicos de control administrativo generados por tus envios."
        icon={IconFileCertificate}
        newLabel="Nuevo DeCA"
        count={decas.length}
        countColor="deca"
        onNew={() => navigate('/deca/nuevo')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <Select
              placeholder="Estado"
              data={ESTADOS_DECA.map((s) => ({ value: s, label: s }))}
              value={estadoFilter}
              onChange={setEstadoFilter}
              clearable
              w={160}
            />
            <TextInput
              placeholder="Buscar por codigo, origen, vehiculo…"
              leftSection={<IconSearch size={16} />}
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
              w={300}
            />
          </Group>
        }
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los DeCA"
        >
          Verifica tu conexión y vuelve a intentarlo.
        </Alert>
      )}

      <Card withBorder>
        {isLoading ? (
          <Center mih={120}>
            <Loader size="sm" />
          </Center>
        ) : (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Codigo</Table.Th>
                  <Table.Th>Estado</Table.Th>
                  <Table.Th>Fecha emision</Table.Th>
                  <Table.Th>Cargador</Table.Th>
                  <Table.Th>Transportista</Table.Th>
                  <Table.Th>Vehiculo</Table.Th>
                  <Table.Th>Conductor</Table.Th>
                  <Table.Th style={{ width: 110, textAlign: 'right' }}>Acciones</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paged.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={8}>
                      {filtered.length === 0
                        ? decas.length === 0
                          ? 'No hay DeCA emitidos todavia. Ve a Envios para emitir uno.'
                          : 'Sin resultados para el filtro.'
                        : 'Sin resultados para el filtro.'}
                    </Table.Td>
                  </Table.Tr>
                )}
                {paged.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {d.codigoAnual ?? d.codigoPublico}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={estadoColor[d.estado] ?? 'gray'} variant="light">
                        {d.estado}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{new Date(d.fechaEmision).toLocaleDateString('es-ES')}</Table.Td>
                    <Table.Td>{d.cargador?.razonSocial ?? '—'}</Table.Td>
                    <Table.Td>{d.transportista?.razonSocial ?? '—'}</Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {d.vehiculo?.matricula ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>{d.conductor?.nombre ?? '—'}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Tooltip label="Ver detalle">
                          <ActionIcon
                            component={Link}
                            to={`/deca/${d.id}`}
                            variant="light"
                            color="deca"
                            aria-label="Ver detalle"
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          label={d.pdfUrl ? 'Descargar PDF' : 'PDF no disponible'}
                          disabled={!d.pdfUrl}
                        >
                          {d.pdfUrl ? (
                            <ActionIcon
                              component="a"
                              href={d.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="light"
                              color="blue"
                              aria-label="Descargar PDF"
                            >
                              <IconDownload size={16} />
                            </ActionIcon>
                          ) : (
                            <ActionIcon
                              variant="light"
                              color="blue"
                              aria-label="Descargar PDF"
                              disabled
                            >
                              <IconDownload size={16} />
                            </ActionIcon>
                          )}
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination value={safePage} onChange={setPage} total={totalPages} />
              </Group>
            )}
          </>
        )}
      </Card>

      <Group justify="flex-end">
        <Button
          variant="light"
          leftSection={<IconDownload size={16} />}
          onClick={() => navigate('/envios')}
        >
          Emitir desde envío
        </Button>
      </Group>
    </Stack>
  );
}
